/////////////////////////////
// Stored workbook data types

/** Workbook version
 *  This version is used to determine compatibility when loading stored files
 *  It consists a {major}.{minor}.
 *  Changes in minor version represent changes that are backward compatible (previous files can be loaded)
 *  but not forward compatible (old versions of the software will not be able to correctly load these files)
 *  Examples of minor version changes are
 *    - the addition of optional fields
 *    - deletion of optional fields
 *  Changes in major version represent changes that are neither forward nor backward compatible (files cannot be loaded
 *  without conversion to the supported major version)
 *  Examples include
 *    - deletion of non-optional fields
 *    - addition of non-optional fields
 *    - name changes
 *    - semantic changes / content expectations (e.g. changing column references from names to ids)
 */
export const WorkbookVersion = "2.2";

export interface SpreadsheetConfig {
  defaultColumnWidth: number;
  defaultRowHeight: number;
}

/*
 * Data type definitions
 */
export interface PropertyDescriptor {
  type: PropertyDescriptorType;
}
export interface FormulaPropertyDescriptor extends PropertyDescriptor {
  expression: string;
}
export interface LookupPropertyDescriptor extends PropertyDescriptor {
  referenceSheet: string;
  keyColumnInCurrentSheet: string;
  keyColumnInReferenceSheet: string;
  referenceColumnInReferenceSheet: string;
}
export interface ChoicePropertyDescriptor extends PropertyDescriptor {
  options: string[];
}
export interface KeyPropertyDescriptor extends PropertyDescriptor {
  referenceSheet: string;
  optionsColumn: string;
}
export type PropertyMap = { [key: string]: PropertyDescriptor };

export enum PropertyDescriptorType {
  General,
  Formula,
  Lookup,
  Choice,
  Key,
}

export const PropertyDescriptorTypes = {
  General: 0,
  Formula: 1,
  Lookup: 2,
  Choice: 3,
  Key: 4,
};

export function isEditablePropertyType(type: PropertyDescriptorType): boolean {
  return [
    PropertyDescriptorType.General,
    PropertyDescriptorType.Choice,
    PropertyDescriptorType.Key,
  ].includes(type); // extend this to more column types later. maybe even use classes and inheritance
}

export function defaultPropertyDescriptor(): PropertyDescriptor {
  return {
    type: PropertyDescriptorType.General,
  };
}

export interface PropertySet {
  descriptors: PropertyMap;
  keyToName: { [key: string]: string };
  nameToKey: { [name: string]: string };
}

/**
 * Map of object primary key to object.
 * Each object is itself a map of property keys to property values.
 */
type PrimaryKey = string;
export type ObjectKey = PrimaryKey;
export type Object = { [key: string]: any };
export type Data = { [row: ObjectKey]: Object };

export type DataSetKey = PrimaryKey;
export interface DataSet {
  naturalOrder?: { [rowIdx: number]: ObjectKey }; // If present, data should be displayed in this order. 0 based
  data: Data;
  properties: PropertySet;
  name: string;
  id: DataSetKey;
}

/*
 * Worksheet/View type definitions
 */
export interface ViewConfig {
  type: string;
  edit?: boolean;
}

export type ViewKey = PrimaryKey;
export interface View {
  config: ViewConfig;
  name: string;
  id: ViewKey;
}

export type WorkbookKey = PrimaryKey;
/**
 * Collection of datasets and views that can be loaded indepedently
 */
export interface Workbook {
  operational: WorkbookOperational;
  data: WorkbookPersistent;
  name: string;
  id: string;
  version: WorkbookKey;
}

/**
 * Elements not saved with the workbook, but recreated on load
 */
export interface WorkbookOperational {
  selectedView: ViewKey;
  nextSheetNumber: number; // Appended to "Sheet" to create initial name for new views
}

/**
 * Elements persisted on safe
 */
export interface WorkbookPersistent {
  viewOrder: string[]; // Used to determine the order of tabs
  views: { [key: ViewKey]: View };
  datasets: { [key: DataSetKey]: DataSet };
}

export type DataCache = { [key: ObjectKey]: Data };
